/**
 * Bulk Payment Import API service (Sales invoices, Excel).
 *
 * Talks to /api/crm/invoices/payments/bulk-import/*.
 */
import { get, post } from '../../apiClient';
import { authFetch } from '../../csrf';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const BASE = `${API_BASE_URL}/crm/invoices/payments/bulk-import`;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const paymentImportService = {
  downloadTemplate: async () => {
    try {
      const response = await authFetch(`${BASE}/template`, { method: 'GET' });
      if (!response.ok) {
        throw new Error('Failed to download template');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const today = new Date();
      const dd = String(today.getDate()).padStart(2, '0');
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      a.href = url;
      a.download = `bulk-payment-import-${dd}-${mm}-${today.getFullYear()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message || 'Failed to download template' };
    }
  },

  validate: async (payments = []) => {
    try {
      const response = await post(`${BASE}/validate`, { payments });
      if (response && response.responseCode === 200) {
        return { success: true, ...response.data };
      }
      return { success: false, error: response?.responseMessage || 'Validation failed' };
    } catch (error) {
      return { success: false, error: error.message || 'Validation failed' };
    }
  },

  /**
   * Start async import and poll until complete.
   * @param {Function} onProgress - ({ current, total, percent, currentInvoice, status })
   */
  importWithProgress: async (payments = [], duplicateDecisions = {}, fileName = '', onProgress) => {
    try {
      const startResponse = await post(`${BASE}`, { payments, duplicateDecisions, fileName });
      if (!startResponse || (startResponse.responseCode !== 200 && startResponse.responseCode !== 201 && startResponse.responseCode !== 202)) {
        return { success: false, error: startResponse?.responseMessage || 'Import failed to start' };
      }

      const batchId = startResponse.data?.batchId;
      if (!batchId) {
        return { success: false, error: 'Import started but no batch ID was returned.' };
      }

      const maxPolls = 600;
      let polls = 0;

      while (polls < maxPolls) {
        polls += 1;
        const progressResponse = await get(`${BASE}/progress/${batchId}`);
        const progress = progressResponse?.data;

        if (!progress) {
          await sleep(400);
          continue;
        }

        onProgress?.({
          current: progress.current || 0,
          total: progress.total || 0,
          percent: progress.percent || 0,
          currentInvoice: progress.currentInvoice || null,
          status: progress.status || 'IMPORTING'
        });

        if (progress.result) {
          return { success: true, ...progress.result };
        }

        if (progress.status === 'FAILED' && progress.error) {
          return { success: false, error: progress.error };
        }

        if (progress.status === 'COMPLETED' || progress.status === 'PARTIAL_SUCCESS') {
          if (progress.result) {
            return { success: true, ...progress.result };
          }
        }

        await sleep(400);
      }

      return { success: false, error: 'Import timed out while waiting for completion.' };
    } catch (error) {
      return { success: false, error: error.message || 'Import failed' };
    }
  }
};

export default paymentImportService;
